if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      this.productInfo = this.productSection.querySelector('.product-info');
      this.productForm = this.productSection.querySelector('product-form');
      this.mainAddButtonWrap = this.productSection.querySelector('.product-info__add-button');
      this.stickySubmit = this.querySelector('.js-sticky-atc-submit');
      this.handleScroll = this.handleScroll.bind(this);
      // Keep the sticky bar's price + option label in sync with the selected variant.
      this.stickyPrice = this.querySelector('.card__info .price');
      this.stickyHeading = this.querySelector('.sticky-atc-panel__heading');
      this.mainPrice = this.productSection.querySelector('.product-info__price .price, .product-review-price-row__price .price');
      this.syncVariant = this.syncVariant.bind(this);

      if ('IntersectionObserver' in window && 'MutationObserver' in window) {
        this.bindEvents();
      }
    }

    /** Mirror the (variant-aware) main price into the sticky bar, and show the chosen option under the title. */
    syncVariant() {
      if (this.stickyPrice && this.mainPrice && this.stickyPrice.innerHTML !== this.mainPrice.innerHTML) {
        this.stickyPrice.innerHTML = this.mainPrice.innerHTML;
      }
      const checked = this.productSection.querySelector('.opt-btn:checked + .opt-label--btn');
      const optText = checked ? (checked.getAttribute('data-option-display-value') || '').trim() : '';
      if (!this.stickyHeading) return;
      let optEl = this.stickyHeading.querySelector('.sticky-atc-panel__option');
      if (optText) {
        if (!optEl) {
          optEl = document.createElement('span');
          optEl.className = 'sticky-atc-panel__option';
          this.stickyHeading.appendChild(optEl);
        }
        optEl.textContent = optText;
      } else if (optEl) {
        optEl.remove();
      }
    }

    // eslint-disable-next-line class-methods-use-this
    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleScroll);
    }

    bindEvents() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!theme.mediaMatches.md && entry.target === this.mainAddButtonWrap) {
            this.classList.toggle('sticky-atc-panel--out', entry.isIntersecting || entry.boundingClientRect.top > 0);
          } else if (!theme.mediaMatches.md && entry.target === this.productForm) {
            this.classList.toggle('sticky-atc-panel--out', entry.boundingClientRect.bottom > 0);
          } else if (entry.target === this.productInfo && theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.isIntersecting);
          }
        });
      }, { threshold: 0 });

      if (this.mainAddButtonWrap) observer.observe(this.mainAddButtonWrap);
      if (!this.mainAddButtonWrap && this.productForm) observer.observe(this.productForm);
      if (this.productInfo) observer.observe(this.productInfo);
      if (this.stickySubmit) this.stickySubmit.addEventListener('click', this.handleStickySubmit.bind(this));

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleScroll);
      this.handleScroll();

      // Update the sticky price + option whenever the shopper changes variant.
      document.addEventListener('on:variant:change', this.syncVariant);
      const idInput = this.productForm?.querySelector('input[name="id"]');
      if (idInput) idInput.addEventListener('change', this.syncVariant);
      window.setTimeout(this.syncVariant, 600); // catch any initial non-default selection
    }

    handleScroll() {
      StickyAtcPanel.handleScroll();

      if (!theme.mediaMatches.md && this.mainAddButtonWrap) {
        const addButtonRect = this.mainAddButtonWrap.getBoundingClientRect();
        this.classList.toggle('sticky-atc-panel--out', addButtonRect.bottom > 16);
      }
    }

    handleStickySubmit(evt) {
      const mainSubmit = this.productForm?.querySelector('[name="add"]');

      if (mainSubmit && !mainSubmit.disabled) {
        evt.preventDefault();
        this.setStickyLoadingState(true);
        mainSubmit.click();
        return;
      }

      const anchorId = this.stickySubmit?.dataset.anchor || 'product-info';
      const anchor = document.getElementById(anchorId);
      if (anchor) {
        evt.preventDefault();
        anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    /**
     * Sets instant loading feedback before the main product form handles the request.
     * @param {boolean} isLoading - Loading state.
     */
    setStickyLoadingState(isLoading) {
      if (!this.stickySubmit) return;

      if (isLoading) {
        this.stickySubmit.dataset.loadingOriginalText = this.stickySubmit.dataset.loadingOriginalText || this.stickySubmit.textContent.trim();
        this.stickySubmit.classList.add('is-loading');
        this.stickySubmit.setAttribute('aria-busy', 'true');
        this.stickySubmit.textContent = 'Adding...';
      } else {
        this.stickySubmit.classList.remove('is-loading');
        this.stickySubmit.removeAttribute('aria-busy');
        if (this.stickySubmit.dataset.loadingOriginalText) {
          this.stickySubmit.textContent = this.stickySubmit.dataset.loadingOriginalText;
          delete this.stickySubmit.dataset.loadingOriginalText;
        }
      }
    }

    /**
     * Watches for a scroll to the bottom of the page
     */
    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
